import { Injectable } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { SensorField } from '../../correlation.tools';

export interface CopiedSensorField {
  fieldName: string;
  displayName: string;
  sensors: Array<{
    checked: boolean;
    id: string;
    name: string;
    influx_name: string;
  }>;
}

@Injectable({
  providedIn: 'root'
})
export class SensorFieldCopyPasteService {
  private copiedField: CopiedSensorField | null = null;

  /**
   * Copy sensor field configuration from source field
   */
  copyField(sourceFieldIndex: number, sensorFieldsFormArray: FormArray): boolean {
    try {
      const sourceFieldGroup = sensorFieldsFormArray.at(sourceFieldIndex) as FormGroup;
      const sourceFieldValue = sourceFieldGroup.value;
      
      this.copiedField = {
        fieldName: sourceFieldValue.fieldName,
        displayName: sourceFieldValue.displayName,
        sensors: sourceFieldValue.sensors.map(sensor => ({
          checked: sensor.checked,
          id: sensor.id,
          name: sensor.name,
          influx_name: sensor.influx_name
        }))
      };
      
      return true;
    } catch (error) {
      console.error('Error copying field:', error);
      return false;
    }
  }

  /**
   * Paste sensor field configuration to target field
   */
  pasteField(targetFieldIndex: number, sensorFieldsFormArray: FormArray): boolean {
    if (!this.copiedField) {
      return false;
    }

    try {
      const targetFieldGroup = sensorFieldsFormArray.at(targetFieldIndex) as FormGroup;
      const targetSensorsArray = targetFieldGroup.get('sensors') as FormArray;
      
      // Update each sensor's checked state based on copied configuration
      targetSensorsArray.controls.forEach((sensorControl, index) => {
        const sensorValue = sensorControl.value;
        
        // Find matching sensor in copied data by influx_name
        const copiedSensor = this.copiedField.sensors.find(
          copied => copied.influx_name === sensorValue.influx_name
        );
        
        if (copiedSensor) {
          sensorControl.get('checked').setValue(copiedSensor.checked);
        }
      });
      
      // Update the allSelected state for the target field
      this.updateAllSelectedState(targetFieldGroup);
      
      return true;
    } catch (error) {
      console.error('Error pasting field:', error);
      return false;
    }
  }

  /**
   * Check if there's a copied field available
   */
  hasCopiedField(): boolean {
    return this.copiedField !== null;
  }

  /**
   * Get information about the copied field
   */
  getCopiedFieldInfo(): string | null {
    return this.copiedField ? this.copiedField.displayName : null;
  }

  /**
   * Clear the copied field
   */
  clearCopiedField(): void {
    this.copiedField = null;
  }

  /**
   * Update the allSelected state based on individual sensor selections
   */
  private updateAllSelectedState(fieldGroup: FormGroup): void {
    const sensorsArray = fieldGroup.get('sensors') as FormArray;
    const allSelected = sensorsArray.controls.every(
      sensorCtrl => sensorCtrl.get('checked').value
    );
    fieldGroup.get('allSelected').setValue(allSelected);
  }

  /**
   * Copy all sensor selections from source to target field
   */
  copyAllSelections(sourceFieldIndex: number, targetFieldIndex: number, sensorFieldsFormArray: FormArray): boolean {
    try {
      const sourceFieldGroup = sensorFieldsFormArray.at(sourceFieldIndex) as FormGroup;
      const targetFieldGroup = sensorFieldsFormArray.at(targetFieldIndex) as FormGroup;
      
      const sourceSensorsArray = sourceFieldGroup.get('sensors') as FormArray;
      const targetSensorsArray = targetFieldGroup.get('sensors') as FormArray;
      
      // Copy all sensor selections
      targetSensorsArray.controls.forEach((targetSensorControl, index) => {
        const targetSensorValue = targetSensorControl.value;
        
        // Find matching sensor in source by influx_name
        const sourceSensorControl = sourceSensorsArray.controls.find(control => 
          control.value.influx_name === targetSensorValue.influx_name
        );
        
        if (sourceSensorControl) {
          const isChecked = sourceSensorControl.get('checked').value;
          targetSensorControl.get('checked').setValue(isChecked);
        }
      });
      
      // Update the allSelected state for the target field
      this.updateAllSelectedState(targetFieldGroup);
      
      return true;
    } catch (error) {
      console.error('Error copying all selections:', error);
      return false;
    }
  }
}
